import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Empty, Tag, Card, Popconfirm } from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  InboxOutlined,
  WarningOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { WorkOrderLineItem } from '../../types';
import { LineItemDrawer } from './LineItemDrawer';
import { AddLineItemFAB } from './AddLineItemFAB';
import { formatCurrency } from '../../utils/formatCurrency';
import LineItemPhotosControl from '../Shared/LineItemPhotosControl';
import TechnicianTimeTracker from '../Shared/TechnicianTimeTracker';
import WorkOrderLineInlineEditor from '../WorkOrder/WorkOrderLineInlineEditor';

export interface LineItemListProps {
  items: WorkOrderLineItem[];
  isLocked: boolean;
  isMobile: boolean;
  onDeleteItem: (id: number) => Promise<void> | void;

  // ── External editor mode (work orders) ──
  // When onEditItem is provided, Add/Edit open a parent-owned full editor
  // (WorkOrderLineItemForm) that supports parts CRUD, labor + per-part
  // discounts, and accurate price breakdowns. The built-in drawer is not used.
  onAddItem?: () => void;
  onEditItem?: (item: WorkOrderLineItem) => void;

  // ── Internal editor mode (estimates) ──
  // When onEditItem is NOT provided, the built-in LineItemDrawer handles edits
  // and saves via onSaveItem. workOrderId is passed through to that drawer.
  onSaveItem?: (values: Partial<WorkOrderLineItem>) => Promise<void>;
  workOrderId?: number;

  // ── Inline editor mode (work orders, estimate-parity rebuild) ──
  // When inlineEditor is true, Add/Edit expand WorkOrderLineInlineEditor
  // directly inside the card (instead of calling onEditItem / opening the
  // Drawer). Takes precedence over the external-editor and drawer paths.
  // workOrderId (above) is required for saves; isOwner gates owner-only
  // fields (part cost) inside the editor; onLineSaved fires after a
  // successful per-item save so the parent can reload.
  inlineEditor?: boolean;
  isOwner?: boolean;
  onLineSaved?: () => void;

  /** External trigger for the inline "new" editor (work orders). A parent
   *  (WO header "Add" button) bumps this counter to open the inline "new"
   *  editor from outside this component, replacing the old onAddItem-driven
   *  Drawer path. 0 = initial, never opens. */
  addNonce?: number;

  /** Task 9: fires whenever the inline editor's own dirty state changes
   *  (true on first edit, false on cancel/save/collapse). The parent
   *  (OptimizedWorkOrderDetail) wires this into its WebSocket edit-guard —
   *  startEditing()/stopEditing() — so an incoming socket update doesn't
   *  blow away an in-progress inline edit. */
  onEditorDirtyChange?: (dirty: boolean) => void;

  /** Optional: called when the user taps "+ Diagnostic" on a row. The parent
   *  only passes this when the shop has diagnostic procedures enabled
   *  (useAiFeatureFlags, migration 195) — its presence IS the gate. */
  onDiagnosticRequested?: (lineItemId: number) => void;

  /** C2-T26: When provided, renders TechnicianTimeTracker + LineItemPhotosControl per item */
  technicianId?: number;

  /** When provided, diagnosis line items (is_diagnosis && !is_diagnosis_waived)
   *  show a "Waive fee" action. Gated upstream by the apply_discounts capability. */
  onWaiveDiagnosis?: (item: WorkOrderLineItem) => void;

  /** When provided, each line can be marked as (or unmarked as) the DIAGNOSIS FEE.
   *
   *  Nothing in the app could set this flag before 2026-07-30, which made the whole
   *  diagnosis-credit feature unreachable: `spawn-repair` sums `is_diagnosis` lines
   *  to compute the credit, so on a real diagnosis work order it always computed
   *  $0 and the customer was billed the diagnosis twice. Gated upstream by
   *  manage_diagnosis. */
  onToggleDiagnosisLine?: (item: WorkOrderLineItem, isDiagnosis: boolean) => void;

  /** Authoritative money rollup for the footer (built from pricing in the parent).
   *  When provided, the footer shows the full chain: Original (gross, pre-discount) →
   *  per-job/Total Discounts → Subtotal (pre-tax) → Tax → Total (with tax). All values
   *  are approved-only (the discount is computed against approved items), so they match
   *  the Financial Summary panel. Omit it (estimate mode) to fall back to a single total. */
  financialSummary?: {
    /** Gross of all approved labor + parts at full price (pre any discount). */
    gross: number;
    /** All discounts combined: per-line labor/part discounts + whole-job discount. */
    totalDiscounts: number;
    /** Whole-job discount portion (subset of totalDiscounts). */
    jobDiscountAmount: number;
    /** Badge for the whole-job discount, e.g. "10%". */
    jobDiscountLabel?: string;
    /** After all discounts, before tax. */
    subtotal: number;
    /** Tax rate as a percent (e.g. 8.25). */
    taxRate: number;
    /** Tax dollar amount. */
    taxAmount: number;
    /** Final total, tax included. */
    total: number;
  };
}

const computeLineTotal = (item: WorkOrderLineItem): number => {
  if (item.line_total != null) return item.line_total;
  const laborTotal = ((item.labor_hours || 0) * (item.labor_rate || 0)) - (item.labor_discount_amount || 0);
  // Respect per-part discounts: prefer the stored per-part total_price, else fall back to
  // gross minus the line-level discount_amount (backend treats discount_amount as line-level,
  // NOT per-unit — see workOrderTotals.ts). The previous `quantity × unit_price` ignored
  // part discounts entirely and overstated discounted lines.
  const partsTotal = item.parts?.reduce((sum, p) => {
    const gross = (p.unit_price || 0) * (p.quantity || 0);
    const final = p.total_price != null ? p.total_price : Math.max(0, gross - (p.discount_amount || 0));
    return sum + final;
  }, 0) || 0;
  return laborTotal + partsTotal;
};

const shopStatusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  new: { color: 'blue', label: 'New', icon: <InboxOutlined /> },
  in_progress: { color: 'processing', label: 'In Progress', icon: <ToolOutlined /> },
  waiting_parts: { color: 'orange', label: 'Waiting Parts', icon: <ClockCircleOutlined /> },
  need_approval: { color: 'warning', label: 'Needs Approval', icon: <WarningOutlined /> },
  completed: { color: 'success', label: 'Completed', icon: <CheckCircleOutlined /> },
};

const approvalColor: Record<string, string> = {
  pending: 'default',
  approved: 'success',
  declined: 'error',
};

/** Unified responsive line item list: cards on mobile, table on desktop. */
export const LineItemList: React.FC<LineItemListProps> = ({
  items,
  isLocked,
  isMobile,
  onDeleteItem,
  onAddItem,
  onEditItem,
  onSaveItem,
  workOrderId,
  onDiagnosticRequested,
  technicianId,
  onWaiveDiagnosis,
  onToggleDiagnosisLine,
  financialSummary,
  inlineEditor,
  isOwner,
  onLineSaved,
  onEditorDirtyChange,
  addNonce,
}) => {
  // Work orders pass onEditItem and use the full external editor; estimates
  // fall back to the built-in drawer. inlineEditor (below) takes precedence
  // over both — it expands WorkOrderLineInlineEditor in-card.
  const useExternalEditor = typeof onEditItem === 'function';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkOrderLineItem | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);

  // ── Inline editor mode: which card (by id, or 'new') has its editor open. ──
  const [expandedEditorId, setExpandedEditorId] = useState<number | 'new' | null>(null);

  // A parent (WO header "Add" button) bumps addNonce to open the inline
  // "new" editor from outside this component. 0 = initial, never opens.
  useEffect(() => {
    if (addNonce) setExpandedEditorId('new');
  }, [addNonce]);

  const buildNewLineItem = useCallback((): WorkOrderLineItem => ({
    work_order_id: workOrderId ?? 0,
    concern: '',
    shop_status: 'new',
    customer_approval_status: 'pending',
    line_number: items.length + 1,
    parts: [],
  }), [workOrderId, items.length]);

  // Both close paths (cancel + saved) always clear the dirty guard, even if
  // the editor's own onDirtyChange(false) call is missed for any reason —
  // the guard must never stay latched once the card has collapsed.
  const closeInlineEditor = useCallback(() => {
    setExpandedEditorId(null);
    onEditorDirtyChange?.(false);
  }, [onEditorDirtyChange]);

  const handleInlineSaved = useCallback(() => {
    setExpandedEditorId(null);
    onEditorDirtyChange?.(false);
    onLineSaved?.();
  }, [onLineSaved, onEditorDirtyChange]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.line_number - b.line_number),
    [items],
  );

  const grandTotal = useMemo(
    () => items.reduce((sum, item) => sum + computeLineTotal(item), 0),
    [items],
  );

  // Footer money chain. With a financialSummary (work orders): Original (gross) →
  // Discounts → Subtotal (pre-tax) → Tax → Total (with tax). The per-line cards already
  // show exactly WHICH labor/parts were discounted and by how much; this footer is the
  // whole-job rollup. Without a summary (estimates) we fall back to a single total.
  const fs = financialSummary;
  const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between' };
  const totalFooter = fs ? (
    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ minWidth: 240, fontSize: 15 }}>
        {fs.totalDiscounts > 0 && (
          <>
            <div style={{ ...rowStyle, color: '#6b7280' }}>
              <span>Original</span>
              <span>{formatCurrency(fs.gross)}</span>
            </div>
            {/* Break out the whole-job discount separately only when per-line discounts
                also exist; otherwise the single Total Discounts line carries its % label. */}
            {fs.jobDiscountAmount > 0 && fs.totalDiscounts - fs.jobDiscountAmount > 0.005 && (
              <div style={{ ...rowStyle, color: '#16a34a', marginTop: 4 }}>
                <span>Whole-job discount{fs.jobDiscountLabel ? ` | ${fs.jobDiscountLabel}` : ''}</span>
                <span>-{formatCurrency(fs.jobDiscountAmount)}</span>
              </div>
            )}
            <div style={{ ...rowStyle, color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
              <span>
                Total Discounts
                {fs.jobDiscountLabel && fs.totalDiscounts - fs.jobDiscountAmount <= 0.005 ? ` | ${fs.jobDiscountLabel}` : ''}
              </span>
              <span>-{formatCurrency(fs.totalDiscounts)}</span>
            </div>
          </>
        )}
        <div style={{
          ...rowStyle,
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid #e5e7eb',
          fontWeight: 600,
          color: '#374151',
        }}>
          <span>Subtotal{fs.taxAmount > 0 ? ' (no tax)' : ''}</span>
          <span>{formatCurrency(fs.subtotal)}</span>
        </div>
        {fs.taxAmount > 0 && (
          <div style={{ ...rowStyle, color: '#6b7280', marginTop: 4 }}>
            <span>Tax{fs.taxRate ? ` (${fs.taxRate}%)` : ''}</span>
            <span>{formatCurrency(fs.taxAmount)}</span>
          </div>
        )}
        <div style={{
          ...rowStyle,
          marginTop: 8,
          paddingTop: 8,
          borderTop: '2px solid #e5e7eb',
          fontSize: 20,
          fontWeight: 700,
          color: '#2563eb',
        }}>
          <span>Total</span>
          <span>{formatCurrency(fs.total)}</span>
        </div>
      </div>
    </div>
  ) : (
    <div style={{
      textAlign: 'right',
      marginTop: 24,
      fontSize: 20,
      fontWeight: 600,
      color: '#2563eb',
    }}>
      Total: {formatCurrency(grandTotal)}
    </div>
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedItem(null);
    setIsNewItem(false);
  }, []);

  const handleAdd = useCallback(() => {
    if (inlineEditor) {
      setExpandedEditorId('new');
    } else if (useExternalEditor) {
      onAddItem?.();
    } else {
      setSelectedItem(null);
      setIsNewItem(true);
      setDrawerOpen(true);
    }
  }, [inlineEditor, useExternalEditor, onAddItem]);

  const handleEdit = useCallback((item: WorkOrderLineItem) => {
    if (inlineEditor) {
      setExpandedEditorId(item.id ?? 'new');
    } else if (useExternalEditor) {
      onEditItem?.(item);
    } else {
      setSelectedItem(item);
      setIsNewItem(false);
      setDrawerOpen(true);
    }
  }, [inlineEditor, useExternalEditor, onEditItem]);

  // Built-in drawer is only mounted in internal-editor mode. Also excluded
  // whenever inlineEditor is set, so the estimate drawer can never mount in
  // inline mode even if the WO stops passing onEditItem.
  const internalDrawer = !useExternalEditor && !inlineEditor && (
    <LineItemDrawer
      open={drawerOpen}
      item={selectedItem}
      isNew={isNewItem}
      isLocked={isLocked}
      workOrderId={workOrderId ?? 0}
      onSave={onSaveItem ?? (async () => {})}
      onDelete={(id) => Promise.resolve(onDeleteItem(id))}
      onClose={closeDrawer}
    />
  );

  // ── Rich line-item card (desktop + mobile share one render path) ──
  const renderLineCard = (item: WorkOrderLineItem) => {
    // Inline editor mode: this card is expanded — render the editor in place
    // of the read-only card body. Requires an id (new items are handled by
    // the standalone editor rendered above the list).
    if (inlineEditor && item.id != null && expandedEditorId === item.id) {
      return (
        <Card key={item.id} size="small" style={{ borderLeft: '3px solid #2563eb' }} styles={{ body: { padding: isMobile ? 12 : 16 } }}>
          <WorkOrderLineInlineEditor
            workOrderId={workOrderId ?? 0}
            lineItem={item}
            isNew={false}
            isOwner={!!isOwner}
            isLocked={isLocked}
            onSaved={handleInlineSaved}
            onCancel={closeInlineEditor}
            onDirtyChange={onEditorDirtyChange}
            existingLineItems={items}
          />
        </Card>
      );
    }

    const laborHours = item.labor_hours || 0;
    const laborRate = item.labor_rate || 0;
    const laborSubtotal = laborHours * laborRate;
    const laborTotal = item.labor_total != null
      ? item.labor_total
      : Math.max(0, laborSubtotal - (item.labor_discount_amount || 0));
    const laborDiscount = Math.max(0, laborSubtotal - laborTotal);

    const parts = item.parts || [];
    const partsGross = parts.reduce((s, p) => s + (p.unit_price || 0) * (p.quantity || 0), 0);
    const partsFinalComputed = parts.reduce((s, p) => {
      const gross = (p.unit_price || 0) * (p.quantity || 0);
      return s + (p.total_price != null ? p.total_price : Math.max(0, gross - (p.discount_amount || 0)));
    }, 0);
    const partsTotal = item.parts_total != null ? item.parts_total : partsFinalComputed;

    const lineTotal = computeLineTotal(item);
    const originalTotal = item.original_line_total != null
      ? item.original_line_total
      : laborSubtotal + partsGross;
    const totalDiscount = Math.max(0, originalTotal - lineTotal);

    const cfg = shopStatusConfig[item.shop_status] || shopStatusConfig.new;
    const waived = !!item.is_diagnosis_waived;

    return (
      <Card
        key={item.id}
        size="small"
        style={{
          borderLeft: waived ? '3px solid #22c55e' : '3px solid #2563eb',
          cursor: isLocked ? 'default' : 'pointer',
        }}
        styles={{ body: { padding: isMobile ? 12 : 16 } }}
        onClick={isLocked ? undefined : () => handleEdit(item)}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#9ca3af', fontSize: 12 }}>#{item.line_number}</span>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{item.concern || 'Untitled'}</span>
              {item.urgency && (
                <Tag color={item.urgency.color} style={{ fontSize: 11, margin: 0 }}>{item.urgency.label}</Tag>
              )}
              {Boolean(item.is_diagnosis) && <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>Diagnosis</Tag>}
            </div>
            {item.correction && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{item.correction}</div>
            )}
            {/*
              WP-10 slice 2 — what the CUSTOMER said about this line.

              Written on the estimate's approval screen and carried across at
              conversion (services/estimateConversion.ts). Placed directly under
              the concern because it is the one thing on this card that came
              from outside the shop: a technician needs to read "only do the
              fronts" before they start, not after.

              Visually distinct from the shop's own `notes`, for the same reason
              the two are separate columns. Guarded on non-empty so the
              overwhelming majority of lines, which have no customer note,
              render exactly as they did before.
            */}
            {item.customer_notes && String(item.customer_notes).trim() !== '' && (
              <div
                data-testid={`wo-line-customer-note-${item.id}`}
                style={{
                  marginTop: 6,
                  padding: '6px 10px',
                  background: '#eff6ff',
                  borderLeft: '3px solid #3b82f6',
                  borderRadius: 4,
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', letterSpacing: 0.4 }}>
                  CUSTOMER NOTE
                </div>
                <div style={{ fontSize: 13, color: '#1e3a5f', whiteSpace: 'pre-wrap' }}>
                  {item.customer_notes}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
            <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>{cfg.label}</Tag>
            <Tag color={approvalColor[item.customer_approval_status] || 'default'} style={{ margin: 0, fontSize: 11 }}>
              {item.customer_approval_status}
            </Tag>
          </div>
        </div>

        {/* Financial breakdown */}
        <div style={{ backgroundColor: waived ? '#f0fdf4' : '#f9fafb', padding: 10, borderRadius: 6, marginBottom: 10 }}>
          {/* Labor */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#3b82f6', marginBottom: 4 }}>Labor</div>
            {laborHours > 0 ? (
              <div style={{ paddingLeft: 8, borderLeft: '2px solid #dbeafe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                  <span>{laborHours} hrs × {formatCurrency(laborRate)}/hr</span>
                  <span>{formatCurrency(laborSubtotal)}</span>
                </div>
                {laborDiscount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#16a34a' }}>
                    <span>Discount{item.labor_discount_percentage ? ` (${item.labor_discount_percentage}%)` : ''}</span>
                    <span>-{formatCurrency(laborDiscount)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#1e40af', marginTop: 2 }}>
                  <span>Labor Total</span>
                  <span>{formatCurrency(laborTotal)}</span>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#9ca3af', paddingLeft: 8 }}>No labor</div>
            )}
          </div>

          {/* Parts */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#7c3aed', marginBottom: 4 }}>Parts ({parts.length})</div>
            {parts.length > 0 ? (
              <div style={{ paddingLeft: 8, borderLeft: '2px solid #ede9fe' }}>
                {parts.map((p, pIdx) => {
                  const gross = (p.unit_price || 0) * (p.quantity || 0);
                  const final = p.total_price != null ? p.total_price : Math.max(0, gross - (p.discount_amount || 0));
                  const disc = Math.max(0, gross - final);
                  return (
                    <div key={pIdx} style={{ marginBottom: pIdx < parts.length - 1 ? 6 : 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                          {p.part_name || 'Part'}{p.part_number ? ` (${p.part_number})` : ''}
                        </span>
                        <span style={{ flexShrink: 0 }}>
                          {(p.quantity || 0) > 1 ? `${p.quantity} × ${formatCurrency(p.unit_price || 0)}` : formatCurrency(p.unit_price || 0)}
                        </span>
                      </div>
                      {disc > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#16a34a', paddingLeft: 8 }}>
                          <span>{p.discount_percentage ? `${p.discount_percentage}% discount` : 'Discount'}</span>
                          <span>-{formatCurrency(disc)}</span>
                        </div>
                      )}
                      {(disc > 0 || (p.quantity || 0) > 1) && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12, fontWeight: 500, color: '#6d28d9' }}>
                          {formatCurrency(final)}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#5b21b6', marginTop: 4, paddingTop: 4, borderTop: '1px solid #ede9fe' }}>
                  <span>Parts Total</span>
                  <span>{formatCurrency(partsTotal)}</span>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#9ca3af', paddingLeft: 8 }}>No parts</div>
            )}
          </div>
        </div>

        {/* Total savings */}
        {totalDiscount > 0 && (
          <div style={{ backgroundColor: '#f0fdf4', padding: '6px 10px', borderRadius: 4, marginBottom: 8, borderLeft: '3px solid #22c55e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>Total Savings</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>-{formatCurrency(totalDiscount)}</span>
          </div>
        )}

        {/* Line total */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '2px solid #e5e7eb' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Line Total</span>
          <div style={{ textAlign: 'right' }}>
            {totalDiscount > 0 && (
              <div style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: 12 }}>{formatCurrency(originalTotal)}</div>
            )}
            <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a' }}>{formatCurrency(lineTotal)}</div>
          </div>
        </div>

        {/* Actions */}
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="small" onClick={() => handleEdit(item)} disabled={isLocked}>Edit</Button>
          <Popconfirm
            title="Delete this line item?"
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => item.id != null && onDeleteItem(item.id)}
            disabled={isLocked}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={isLocked}>Delete</Button>
          </Popconfirm>
          {onDiagnosticRequested && item.id != null && (
            <Button size="small" icon={<ToolOutlined />} onClick={() => onDiagnosticRequested(item.id!)}>Diagnostic</Button>
          )}
          {onToggleDiagnosisLine && !item.is_diagnosis_waived && (
            <Button
              size="small"
              disabled={isLocked}
              type={item.is_diagnosis ? 'primary' : 'default'}
              data-testid={`toggle-diagnosis-${item.id}`}
              onClick={() => onToggleDiagnosisLine(item, !item.is_diagnosis)}
            >
              {item.is_diagnosis ? 'Diagnosis fee ✓' : 'Mark as diagnosis fee'}
            </Button>
          )}
          {onWaiveDiagnosis !== undefined && Boolean(item.is_diagnosis) && !item.is_diagnosis_waived && (
            <Button size="small" disabled={isLocked} onClick={() => onWaiveDiagnosis(item)}>Waive fee</Button>
          )}
          {Boolean(item.is_diagnosis_waived) && <Tag color="green" style={{ margin: 0 }}>Diagnosis waived</Tag>}
        </div>

        {/* Per-item photos + time tracker */}
        {item.id && workOrderId && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <LineItemPhotosControl lineItemId={item.id} phase="finding" />
            {technicianId && !isLocked && (
              <TechnicianTimeTracker workOrderId={workOrderId} lineItemId={item.id} technicianId={technicianId} />
            )}
          </div>
        )}
      </Card>
    );
  };

  // Inline editor mode: standalone card for a brand-new (unsaved) line item.
  const newItemEditor = inlineEditor && expandedEditorId === 'new' && (
    <Card size="small" style={{ borderLeft: '3px solid #2563eb', marginBottom: isMobile ? 8 : 12 }} styles={{ body: { padding: isMobile ? 12 : 16 } }}>
      <WorkOrderLineInlineEditor
        workOrderId={workOrderId ?? 0}
        lineItem={buildNewLineItem()}
        isNew
        isOwner={!!isOwner}
        isLocked={isLocked}
        onSaved={handleInlineSaved}
        onCancel={closeInlineEditor}
        onDirtyChange={onEditorDirtyChange}
        existingLineItems={items}
      />
    </Card>
  );

  // ── Empty state ──
  if (items.length === 0) {
    if (newItemEditor) {
      return <div>{newItemEditor}{internalDrawer}</div>;
    }
    return (
      <Card styles={{ body: { padding: isMobile ? '40px 16px' : '50px 24px' } }} style={{ overflow: 'hidden' }}>
        <Empty
          description={
            <>
              <div style={{ fontSize: isMobile ? 14 : 15, color: '#6b7280' }}>No line items yet</div>
              <div style={{ fontSize: isMobile ? 12 : 13, color: '#9ca3af' }}>
                {isMobile
                  ? 'Tap the + button to add parts, labor, or services.'
                  : 'Click Add Line Item to add parts, labor, or services.'}
              </div>
            </>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {!isMobile && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={isLocked}
              style={{ marginTop: 14 }}
            >
              Add First Line Item
            </Button>
          )}
        </Empty>
        {isMobile && <AddLineItemFAB onClick={handleAdd} disabled={isLocked} />}
        {internalDrawer}
      </Card>
    );
  }

  // ── Card list (desktop + mobile) ──
  return (
    <div>
      {!isMobile && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={isLocked}>
            Add Line Item
          </Button>
        </div>
      )}

      {newItemEditor}

      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 12 }}>
        {sortedItems.map(renderLineCard)}
      </div>

      {/* Grand total */}
      {totalFooter}

      {isMobile && <AddLineItemFAB onClick={handleAdd} disabled={isLocked} />}

      {internalDrawer}
    </div>
  );
};

export default LineItemList;
